import { useEffect, useRef } from 'react';

const AboutSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: 'ri-lightbulb-flash-fill',
      title: 'Innovation',
      description: 'Repousser les limites de la créativité',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'ri-heart-3-fill',
      title: 'Passion',
      description: 'Chaque projet est une œuvre d\'art',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: 'ri-star-fill',
      title: 'Excellence',
      description: 'Qualité premium sans compromis',
      color: 'from-orange-500 to-yellow-500'
    },
    {
      icon: 'ri-team-fill',
      title: 'Collaboration',
      description: 'Ensemble, créons l\'extraordinaire',
      color: 'from-blue-500 to-cyan-500'
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-32 px-4 bg-gradient-to-b from-[#0A0A0A] to-[#0F0F0F] overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#C0C0C0]/10 to-[#FFD700]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20 animate-on-scroll opacity-0">
          <div className="inline-block mb-6">
            <span className="text-[#FFD700] text-sm font-bold tracking-widest uppercase">À Propos</span>
            <div className="h-px w-full bg-gradient-to-r from-transparent via-[#FFD700] to-transparent mt-2"></div>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            L'Histoire de <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">VYBE PARIS</span>
          </h2>
        </div>

        {/* Main Content with Glassmorphism */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Text Content */}
          <div className="animate-on-scroll opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="space-y-6">
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                <span className="text-[#FFD700] font-bold">VYBE PARIS</span> est né d'une vision audacieuse : 
                créer un écosystème créatif unique où <span className="text-white font-semibold">événementiel d'exception</span>, 
                <span className="text-white font-semibold"> aventures urbaines</span> et 
                <span className="text-white font-semibold"> création digitale</span> se rencontrent.
              </p>
              
              <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                Basée au cœur de Paris, notre maison créative réunit des talents passionnés 
                dédiés à transformer chaque moment en expérience mémorable. De nos soirées 
                exclusives sur péniches aux balades en quad électrique, en passant par nos 
                campagnes digitales innovantes, nous cultivons l'art de l'excellence.
              </p>

              <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                Chaque projet est une opportunité de repousser les limites, d'innover et 
                de créer des connexions authentiques. Notre approche premium allie créativité 
                sans compromis et exécution impeccable.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                {[
                  { number: '500+', label: 'Événements' },
                  { number: '2K+', label: 'Clients Satisfaits' },
                  { number: '3', label: 'Univers Uniques' }
                ].map((stat, idx) => (
                  <div key={stat.label} className="text-center group">
                    <div className="text-4xl md:text-5xl font-black bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
                      {stat.number}
                    </div>
                    <div className="text-white/60 text-sm font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Image with 3D Effect */}
          <div className="animate-on-scroll opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="relative group perspective-1000">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] opacity-0 group-hover:opacity-75 blur-3xl transition-opacity duration-700 rounded-3xl"></div>
              
              <div className="relative overflow-hidden rounded-3xl transform group-hover:scale-105 transition-all duration-700">
                <img
                  src="https://public.readdy.ai/ai/img_res/08483eca554ca9c88559ac85c4f38de6.jpg"
                  alt="VYBE PARIS Team"
                  className="w-full h-[600px] object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
                
                {/* Floating Badge */}
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 shadow-2xl">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center">
                        <i className="ri-award-fill text-3xl text-white"></i>
                      </div>
                      <div>
                        <div className="text-white font-bold text-lg">Excellence Reconnue</div>
                        <div className="text-white/70 text-sm">Leader de l'expérience premium</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Values Section with 3D Cards */}
        <div className="animate-on-scroll opacity-0" style={{ animationDelay: '0.6s' }}>
          <h3 className="text-4xl md:text-5xl font-black text-white text-center mb-16" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Nos <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">Valeurs</span>
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div
                key={value.title}
                className="group relative"
                style={{ animationDelay: `${0.8 + idx * 0.1}s` }}
              >
                <div className={`absolute -inset-1 bg-gradient-to-r ${value.color} opacity-0 group-hover:opacity-75 blur-xl transition-opacity duration-500 rounded-2xl`}></div>
                
                <div className="relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:scale-105 transition-all duration-500 h-full">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-500 shadow-lg`}>
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <h4 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {value.title}
                  </h4>
                  
                  <p className="text-white/70 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mt-24 text-center animate-on-scroll opacity-0" style={{ animationDelay: '1s' }}>
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#FFA500] blur-3xl opacity-30"></div>
            <blockquote className="relative text-2xl md:text-4xl font-light text-white/90 italic max-w-4xl px-8 py-6">
              "Créer des expériences qui transcendent l'ordinaire et laissent une empreinte indélébile"
            </blockquote>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }
        
        .perspective-1000 {
          perspective: 1000px;
        }
      `}</style>
    </section>
  );
};

export default AboutSection;
